import { Services } from "@/models/Service";
import {
  Menu,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  PositioningImperativeRef,
} from "@fluentui/react-components";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  LexicalNode,
  TextNode,
} from "lexical";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { $createMentionNode, $isMentionNode } from "./MentionNode";

type Props = {
  services?: Services;
  editorRef?: React.RefObject<HTMLDivElement>;
  onMentionChange: (newRecipientId?: string) => void;
  selectedService?: { id: string; name: string; };
};

export const MentionPlugin: React.FC<Props> = ({ services, editorRef, onMentionChange, selectedService }) => {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [atSymbolPosition, setAtSymbolPosition] = useState<number>(-1);
  const [currentNodeKey, setCurrentNodeKey] = useState<string>("");
  const menuPositioningRef = useRef<PositioningImperativeRef>(null);

  // Track previous selectedService to avoid unnecessary updates
  const prevSelectedServiceRef = useRef<{ id: string; name: string; } | undefined>(selectedService);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          setShowMenu(false);
          return;
        }

        const anchor = selection.anchor;
        const focus = selection.focus;

        // カーソル位置の場合のみ処理を続行
        if (anchor.offset !== focus.offset || anchor.key !== focus.key) {
          setShowMenu(false);
          return;
        }

        const node = anchor.getNode();

        if (!(node instanceof TextNode)) {
          setShowMenu(false);
          return;
        }

        // Track current node key for replacing text later
        setCurrentNodeKey(node.getKey());

        const text = node.getTextContent();
        const cursorPosition = anchor.offset;

        // カーソル前の最後の@記号を検索
        const lastAtSymbol = text.lastIndexOf("@", cursorPosition - 1);

        // @記号があり、現在の単語の一部である場合
        if (
          lastAtSymbol !== -1
          && (lastAtSymbol === 0 || /\s/.test(text.charAt(lastAtSymbol - 1)))
        ) {
          // @とカーソル位置の間のクエリテキストを抽出
          const mentionQuery = text.substring(lastAtSymbol + 1, cursorPosition);
          setQuery(mentionQuery);
          setAtSymbolPosition(lastAtSymbol);
          setShowMenu(true);
        } else {
          setShowMenu(false);
        }
      });
    });
  }, [editor]);

  // Function to update the editor with a service mention
  const updateEditorWithMention = useCallback((serviceName: string) => {
    editor.update(() => {
      try {
        // First, find and remove the "@query" text if this was triggered by user selection
        if (currentNodeKey) {
          const textNode = $getNodeByKey(currentNodeKey);

          if (textNode instanceof TextNode) {
            const text = textNode.getTextContent();

            // Remove the "@query" text
            const beforeText = text.substring(0, atSymbolPosition);
            const afterText = text.substring(atSymbolPosition + query.length + 1); // +1 for the @ symbol
            textNode.setTextContent(beforeText + afterText);
          }
        }

        // Get the root node of the editor
        const root = $getRoot();

        // Find and remove all existing MentionNodes in the editor
        root.getChildren().forEach(paragraph => {
          if (!$isParagraphNode(paragraph)) {
            return;
          }
          // Keep track of nodes to remove to avoid mutation during iteration
          const nodesToRemove: LexicalNode[] = [];

          // Find all MentionNodes
          paragraph.getChildren().forEach(node => {
            if ($isMentionNode(node)) {
              nodesToRemove.push(node);
            }
          });

          // Remove all found MentionNodes
          nodesToRemove.forEach(node => node.remove());
        });

        // Create the new mention node with the selected service name
        const newMentionNode = $createMentionNode(serviceName);

        // Get or create a paragraph node
        let paragraph = root.getFirstChild();
        if (!paragraph || !$isParagraphNode(paragraph)) {
          paragraph = $createParagraphNode();
          root.append(paragraph);
        }

        // Make sure we have a valid paragraph node
        if (!$isParagraphNode(paragraph)) {
          throw new Error("Failed to create a valid paragraph node");
        }

        // Get the first child in the paragraph if it exists
        const firstChild = paragraph.getFirstChild();
        let spaceNode = null;

        if (firstChild) {
          // Insert the mention node at the beginning of the paragraph
          paragraph.splice(0, 0, [newMentionNode]);

          // Add a space after the mention if there isn't one already
          const afterText = firstChild.getTextContent();
          if (afterText && !afterText.startsWith(" ")) {
            spaceNode = $createTextNode(" ");
            newMentionNode.insertAfter(spaceNode);
          }
        } else {
          // If paragraph is empty, append the mention node and a space
          paragraph.append(newMentionNode);
          spaceNode = $createTextNode(" ");
          paragraph.append(spaceNode);
        }

        // Move the selection to the end of the editor
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          return;
        }
        // Find the last paragraph (using the root variable already declared above)
        const lastChild = root.getLastChild();

        if (lastChild && $isParagraphNode(lastChild)) {
          // Position cursor at the end of the paragraph (after all its content)
          const childrenLength = lastChild.getChildrenSize();
          selection.focus.set(lastChild.getKey(), childrenLength, "element");
          selection.anchor.set(lastChild.getKey(), childrenLength, "element");
        }
      } catch (error) {
        console.error("Error in updateEditorWithMention:", error);
      }
    });
  }, [editor, currentNodeKey, atSymbolPosition, query]);

  // Effect to handle externally determined service
  useEffect(() => {
    if (selectedService) {
      // Skip if the service hasn't actually changed
      if (prevSelectedServiceRef.current?.id === selectedService.id) {
        return;
      }

      updateEditorWithMention(selectedService.name);

      // Update the ref to the current value
      prevSelectedServiceRef.current = selectedService;
    }
  }, [selectedService, updateEditorWithMention]);

  // レンダリング完了後にメニュー位置のリファレンスをセット
  useEffect(() => {
    if (editorRef?.current) {
      try {
        if (editorRef.current) {
          // Update menu positioning reference
          if (menuPositioningRef.current) {
            menuPositioningRef.current.setTarget(editorRef.current);
          }
        }
      } catch (error) {
        console.error("Error positioning menu:", error);
      }
    }
  });

  // サービス候補のフィルタリング
  const suggestions = services
    ? services.filter((service) => service.name.toLowerCase().includes(query.toLowerCase()))
    : [];

  const handleServiceSelect = (serviceId: string, serviceName: string) => {
    console.log(`Service selected: ${serviceName} (${serviceId})`);

    // Update the editor with the mention
    updateEditorWithMention(serviceName);

    // Notify parent component about the change
    onMentionChange(serviceId);
    setShowMenu(false);
  };

  if (!services || suggestions.length === 0) {
    return null;
  }

  return (
    <Menu
      open={showMenu}
      onOpenChange={(e, data) => setShowMenu(data.open)}
      positioning={{
        positioningRef: menuPositioningRef,
        autoSize: true,
      }}
    >
      <MenuTrigger disableButtonEnhancement>
        <div style={{ display: "none" }} />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuGroupHeader>サービスを選択</MenuGroupHeader>
          {suggestions.map((service) => (
            <MenuItem
              key={service.id}
              onClick={() => handleServiceSelect(service.id, service.name)}
            >
              {service.name}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};
